import { Component, Fragment, useState } from 'react';
import { NotificationCount, PhinNotificationBadge } from '@belong/ai-pointer';
import { Button } from '@belong/ui';
import classNames from 'classnames/bind';
import { clsx } from 'clsx';
import { Tag } from 'components/HomeTag/HomeTag';
import {
  MENU_TYPES,
  menuShapeItem,
  menuShape,
} from 'components/Menu/menuTypes';
import ButtonBase from 'corecomponents/ButtonBase/ButtonBase';
import LinkBase from 'corecomponents/LinkBase/LinkBase';
import styles from './Menu.module.css';

const cx = classNames.bind(styles);

const MENU_ITEMS_PAGE_SIZE = 8;

const MenuItem = ({
  item,
  menuType,
  menuItems,
  onClick,
  onSelect,
  selected,
  menuControlled,
}) => {
  let removePadding;

  if (item.header === '') {
    removePadding = true;
  }

  const inStyleChanges = `${menuType}Style`;
  const MenuItemComponent = item.to ? LinkBase : ButtonBase;

  let hideTransparentBackground = false;
  const isGroup = item.menuType === MENU_TYPES.BORDER;
  const hasBadge = item.badgeCount > 0;
  const [isHovered, setIsHovered] = useState(false);

  if (menuType === MENU_TYPES.BORDER || menuType === MENU_TYPES.BORDER_NESTED) {
    hideTransparentBackground = true;
  }

  return (
    // It might be better to use a react fragment if the header is blank?
    <li
      className={cx('default-background relative', {
        hideTransparentBackground,
        nested: menuType === MENU_TYPES.BORDER_NESTED,
        grouped: isGroup,
        'bg-light-gray rounded-small overflow-hidden pt-sm mb-xs mt-xs first:mt-0':
          isGroup,
        'group rounded-small outline-dashed outline-1 outline-red -outline-offset-1 transition-[outline-color] duration-hover hover:outline-dark-red focus-within:outline-dark-red':
          hasBadge,
      })}
      onMouseEnter={hasBadge ? () => setIsHovered(true) : undefined}
      onMouseLeave={hasBadge ? () => setIsHovered(false) : undefined}
      onFocus={hasBadge ? () => setIsHovered(true) : undefined}
      onBlur={hasBadge ? () => setIsHovered(false) : undefined}
    >
      {item.header && (
        <Fragment>
          {item.redIndicator && (
            <div className={cx('redIndicator', { selected })} />
          )}
          <MenuItemComponent
            to={item.to}
            onClick={() => {
              onClick(item);
              onSelect(item);
              item.onClick?.();
            }}
            className={cx(
              'menuItem',
              inStyleChanges,
              { selected },
              { removePadding },
              hasBadge && 'py-[9px]',
            )}
          >
            {hasBadge ? (
              <span className="flex items-center justify-between gap-xs pr-sm">
                <span
                  className={clsx(
                    'truncate font-semibold transition-colors duration-hover text-red group-hover:text-dark-red group-focus-within:text-dark-red',
                  )}
                >
                  {item.header}
                </span>
                <PhinNotificationBadge
                  count={item.badgeCount}
                  isHovered={isHovered}
                />
              </span>
            ) : item.approvalCount > 0 ? (
              <span className="flex items-center justify-between gap-xs pr-sm">
                <span className="truncate">{item.header}</span>
                <NotificationCount count={item.approvalCount} />
              </span>
            ) : (
              item.header
            )}
            {item.new && <Tag className={cx('tag-new')}>NEW</Tag>}
          </MenuItemComponent>
        </Fragment>
      )}
      {menuItems.length > 0 && (
        <Menu
          onSelect={onSelect}
          controlled={menuControlled}
          selected={item.selected}
          alwaysOpen={item.alwaysOpen}
          menuItems={menuItems}
          menuType={item.menuType}
          menuHideOverFive={item.menuHideOverFive}
        />
      )}
    </li>
  );
};
MenuItem.propTypes = menuShapeItem;
MenuItem.defaultProps = {
  callKeyOnSelect: () => {},
  onClick: () => {},
  selectKey: -1,
  fetchSelect: false,
  menuHideOverFive: false,
  menuItems: [],
  menuType: MENU_TYPES.BACKGROUND,
  new: false,
};

export default class Menu extends Component {
  static propTypes = menuShape;

  static defaultProps = {
    menuType: MENU_TYPES.BACKGROUND,
    menuHideOverFive: false,
    onSelect: () => {},
  };

  constructor(props) {
    super(props);
    this.state = {
      selected: props.selected,
      visibleCount: MENU_ITEMS_PAGE_SIZE,
    };
  }

  static getDerivedStateFromProps(nextProps) {
    if (!nextProps.controlled) {
      return null;
    }

    return {
      selected: nextProps.selected,
    };
  }

  handleClick = (item) => {
    this.setState({ selected: item.key });
  };

  showMore = () => {
    this.setState(({ visibleCount }) => ({
      visibleCount: visibleCount + MENU_ITEMS_PAGE_SIZE,
    }));
  };

  render() {
    const { menuItems, menuType, onSelect, controlled, menuHideOverFive } =
      this.props;
    const { selected, visibleCount } = this.state;
    const limitedMenuItems = menuHideOverFive
      ? menuItems.slice(0, visibleCount)
      : menuItems;
    return (
      <ul className={cx('menu', menuType)}>
        {limitedMenuItems.map((item, index) => {
          return (
            <MenuItem
              key={`${item.key} ${index}`}
              item={item}
              menuItems={
                selected === item.key || item.alwaysOpen ? item.menuItems : []
              }
              menuType={menuType}
              menuControlled={controlled}
              onClick={this.handleClick}
              onSelect={onSelect}
              selected={selected === item.key}
              new={item.new}
            />
          );
        })}
        {limitedMenuItems.length < menuItems.length && (
          <li className="pb-xl pr-2sm flex justify-end">
            <Button variant="text" onClick={this.showMore}>
              {`Show More (+${menuItems.length - limitedMenuItems.length})`}
            </Button>
          </li>
        )}
      </ul>
    );
  }
}
